/**
 * CustomerIdentityContext
 *
 * Lightweight customer identity for the loyalty flow:
 *   - `customerPhone` (digits-only, e.g. "11999998888")
 *     persisted in sessionStorage so it survives tab refreshes within a
 *     single browser session.
 *   - `firebaseUser` (firebase.auth.User | null) — populated whenever the
 *     customer has completed phone OTP verification (Firebase Auth phone).
 *   - `isPhoneVerified()` — true when the verified Firebase Auth phone
 *     matches the current `customerPhone`.
 *
 * Two distinct levels of "identity":
 *   1. Phone known (anonymous) — read-only operations like fetching the
 *      loyalty balance and listing rewards. Set via `setCustomerPhone()`.
 *   2. Phone verified (Firebase Auth phone) — required by the server to
 *      redeem a reward in `placeOrder`. Set via `signInWithPhoneNumber` flow
 *      inside the PhoneVerificationModal; reflected here automatically via
 *      `onAuthStateChanged`.
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { loadAuth } from '@/lib/firebaseAuth';
import { fromE164, toE164 } from '@/utils/phoneFormat';

const STORAGE_KEY = 'dinelink:customerPhone';
const STORAGE_KEY_NAME = 'dinelink:customerName';
const STORAGE_KEY_ADDRESS = 'dinelink:lastDeliveryAddress';
const STORAGE_KEY_PERSON = 'dinelink:personId';

const CustomerIdentityContext = createContext(null);

export function useCustomerIdentity() {
  const ctx = useContext(CustomerIdentityContext);
  if (!ctx) {
    throw new Error('useCustomerIdentity must be used within CustomerIdentityProvider');
  }
  return ctx;
}

export function CustomerIdentityProvider({ children }) {
  const [customerPhone, setCustomerPhoneState] = useState(() => {
    try {
      // Normalize to domestic-only digits on read so we never carry a stale
      // 13-digit value forward (the source of the duplicated "55" bug).
      return fromE164(sessionStorage.getItem(STORAGE_KEY) || '');
    } catch {
      return '';
    }
  });
  const [customerName, setCustomerNameState] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY_NAME) || ''; } catch { return ''; }
  });
  const [personId, setPersonIdState] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY_PERSON) || ''; } catch { return ''; }
  });
  const [lastDeliveryAddress, setLastDeliveryAddressState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_ADDRESS);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [firebaseUser, setFirebaseUser] = useState(null);

  // Track Firebase Auth state — populated by signInWithPhoneNumber.
  // Only spin up Firebase Auth when there's a saved phone (returning loyalty
  // customer). Anonymous menu visitors never load the Auth SDK — that keeps
  // ~131 KB (auth iframe + gapi) off the critical path for the common case.
  useEffect(() => {
    if (!customerPhone) {
      setFirebaseUser(null);
      return undefined;
    }
    let unsub = null;
    let cancelled = false;
    (async () => {
      const { auth, onAuthStateChanged } = await loadAuth();
      if (cancelled) return;
      setFirebaseUser(auth.currentUser);
      unsub = onAuthStateChanged(auth, setFirebaseUser);
    })();
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [customerPhone]);

  const setCustomerPhone = useCallback((phone) => {
    // Always store domestic-only digits (e.g. "11999998888"), regardless of
    // whether the caller passes an E.164 string, a 13-digit number, or the
    // domestic 11-digit form. Prevents the "55" country code from
    // accumulating across round-trips through the cart/header.
    const digits = fromE164(phone);
    setCustomerPhoneState(digits);
    try {
      if (digits) sessionStorage.setItem(STORAGE_KEY, digits);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch { /* storage may be unavailable */ }
  }, []);

  const setCustomerName = useCallback((name) => {
    const trimmed = (name || '').trim();
    setCustomerNameState(trimmed);
    try {
      if (trimmed) sessionStorage.setItem(STORAGE_KEY_NAME, trimmed);
      else sessionStorage.removeItem(STORAGE_KEY_NAME);
    } catch { /* ignore */ }
  }, []);

  const setPersonId = useCallback((id) => {
    const val = (id || '').trim();
    setPersonIdState(val);
    try {
      if (val) sessionStorage.setItem(STORAGE_KEY_PERSON, val);
      else sessionStorage.removeItem(STORAGE_KEY_PERSON);
    } catch { /* ignore */ }
  }, []);

  const setLastDeliveryAddress = useCallback((address) => {
    setLastDeliveryAddressState(address || null);
    try {
      if (address) sessionStorage.setItem(STORAGE_KEY_ADDRESS, JSON.stringify(address));
      else sessionStorage.removeItem(STORAGE_KEY_ADDRESS);
    } catch { /* ignore */ }
  }, []);

  const isPhoneVerified = useCallback(() => {
    if (!firebaseUser?.phoneNumber || !customerPhone) return false;
    return firebaseUser.phoneNumber === toE164(customerPhone);
  }, [firebaseUser, customerPhone]);

  const signOut = useCallback(async () => {
    try {
      const { auth, signOut: fbSignOut } = await loadAuth();
      await fbSignOut(auth);
    } catch { /* ignore */ }
    setCustomerPhone('');
    setCustomerName('');
    setPersonId('');
    setLastDeliveryAddress(null);
  }, [setCustomerPhone, setCustomerName, setPersonId, setLastDeliveryAddress]);

  const value = useMemo(
    () => ({
      customerPhone,
      setCustomerPhone,
      customerName,
      setCustomerName,
      personId,
      setPersonId,
      lastDeliveryAddress,
      setLastDeliveryAddress,
      firebaseUser,
      isPhoneVerified,
      signOut,
    }),
    [
      customerPhone, setCustomerPhone,
      customerName, setCustomerName,
      personId, setPersonId,
      lastDeliveryAddress, setLastDeliveryAddress,
      firebaseUser, isPhoneVerified, signOut,
    ],
  );

  return (
    <CustomerIdentityContext.Provider value={value}>
      {children}
    </CustomerIdentityContext.Provider>
  );
}

export default CustomerIdentityProvider;
